import { useState, useRef, useCallback, useEffect } from 'react';
import { Upload, Loader2, CheckCircle2, AlertTriangle, X, FolderOpen, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { useAuth } from '../../services/auth';
import { useCredits } from '../hooks/credits';
import { CreditWarning } from './creditstatus';
import { cn } from '../../lib/utils';

const ALLOWED_AUDIO_TYPES = ['audio/wav', 'audio/mp3'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export default function UploadSection() {
  const { processCredits, refundCredits, hasEnoughCredits } = useCredits();
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState<'idle' | 'uploading' | 'uploaded' | 'error'>('idle');
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController>();

  const { refreshJWT, isAuthenticated } = useAuth();

  const BASE_URL =
    process.env.NODE_ENV === 'development'
      ? 'http://localhost:3004'
      : process.env.REACT_APP_BACKEND_URL;

  // Cleanup function
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => cleanup, [cleanup]);

  const validateFile = useCallback((file: File): string | null => {
    if (!ALLOWED_AUDIO_TYPES.includes(file.type)) {
      return 'Please upload a valid audio file (MP3 or WAV).';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size must be less than 50MB.';
    }
    return null;
  }, []);

  const createAxiosInstance = useCallback(async () => {
    try {
      const token = await refreshJWT();
      abortControllerRef.current = new AbortController();

      return axios.create({
        baseURL: BASE_URL,
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
        signal: abortControllerRef.current.signal,
      });
    } catch (error) {
      console.error('Failed to create axios instance:', error);
      throw error;
    }
  }, [refreshJWT]);

  const handleAuthError = useCallback((error: any) => {
    if (error?.response?.status === 401) {
      cleanup();
      setProcessingStatus('error');
      setErrorMessage('Authentication expired. Please log in again.');
      toast.error('Authentication expired. Please log in again.');
      return true;
    }
    return false;
  }, [cleanup]);

  const resetUpload = useCallback(() => {
    cleanup();
    setProcessingStatus('idle');
    setUploadProgress(0);
    setErrorMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [cleanup]);

  const handleUpload = useCallback(
    async (file: File) => {
      if (!file || isUploading || !isAuthenticated) return;

      const validationError = validateFile(file);
      if (validationError) {
        toast.error(validationError);
        return;
      }

      try {
        const success = await processCredits();
        if (!success) {
          toast.error('Insufficient credits to process this file');
          return;
        }

        cleanup();
        setIsUploading(true);
        setProcessingStatus('uploading');
        setUploadProgress(0);
        setErrorMessage('');

        const axiosInstance = await createAxiosInstance();
        const formData = new FormData();
        formData.append('audio', file);

        await axiosInstance.post('/recording/upload', formData, {
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
            setUploadProgress(percentCompleted);
          },
          timeout: 30000,
        });

        // Upload complete
        setProcessingStatus('uploaded');
        toast.success('File uploaded successfully. Please check in My Creations');
      } catch (error: any) {
        if (!handleAuthError(error)) {
          await refundCredits(); // Refund credits on failure
          setProcessingStatus('error');
          const errorMessage = error.response?.data?.error || 'Upload failed. Please try again.';
          setErrorMessage(errorMessage);
          toast.error(errorMessage);
        }
      } finally {
        setIsUploading(false);
      }
    },
    [processCredits, refundCredits, createAxiosInstance, handleAuthError, isAuthenticated, cleanup, validateFile]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!hasEnoughCredits()) {
        toast.error('You need at least 2 credits to process an audio file');
        e.target.value = '';
        return;
      }

      const files = e.target.files;
      if (files && files.length > 0) {
        handleUpload(files[0]);
      }
    },
    [handleUpload, hasEnoughCredits]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isUploading) setIsDragging(true);
    },
    [isUploading]
  );

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (!hasEnoughCredits()) {
        toast.error('You need at least 2 credits to process an audio file');
        return;
      }

      if (!isUploading && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleUpload(e.dataTransfer.files[0]);
      }
    },
    [handleUpload, isUploading, hasEnoughCredits]
  );

  const getStatusDisplay = useCallback(() => {
    switch (processingStatus) {
      case 'uploading':
        return (
          <div className="flex items-center justify-center gap-2 text-primary">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Uploading... {uploadProgress}%</span>
          </div>
        );
      case 'uploaded':
        return (
          <div className="flex items-center justify-center gap-2 text-green-500">
            <CheckCircle2 className="w-4 h-4" />
            <span>Upload complete! Please check in My Creations after some time.</span>
          </div>
        );
      case 'error':
        return (
          <div className="flex items-center justify-center gap-2 text-red-500">
            <AlertTriangle className="w-4 h-4" />
            <span>{errorMessage || 'Upload Failed'}</span>
          </div>
        );
      default:
        return null;
    }
  }, [processingStatus, uploadProgress, errorMessage]);

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="relative w-full max-w-lg mx-auto">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-8 h-8 sm:w-12 sm:h-12 bg-orange-400/20 rounded-br-[50px]" />
        <div className="absolute top-0 right-0 w-8 h-8 sm:w-12 sm:h-12 bg-orange-400/20 rounded-bl-[50px]" />
        <div className="absolute bottom-0 left-0 w-8 h-8 sm:w-12 sm:h-12 bg-orange-400/20 rounded-tr-[50px]" />
        <div className="absolute bottom-0 right-0 w-8 h-8 sm:w-12 sm:h-12 bg-orange-400/20 rounded-tl-[50px]" />
      </div>

      <div className="relative bg-white rounded-2xl shadow-sm p-4 sm:p-6 min-h-[300px]">
        <CreditWarning />
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg sm:text-xl font-semibold">Upload your files</h2>
          {processingStatus !== 'idle' && (
            <Button variant="ghost" size="sm" onClick={resetUpload} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>

        <div
          className={cn(
            'relative border-2 border-dashed rounded-xl p-4 sm:p-6 text-center transition-all duration-300',
            isDragging ? 'border-primary bg-primary/5 scale-[0.99]' : 'border-gray-200',
            processingStatus !== 'idle' ? 'opacity-50 cursor-not-allowed' : 'hover:border-primary/50 hover:bg-gray-50/50 cursor-pointer'
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => !isUploading && handleBrowseClick()}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            className="hidden"
            accept={ALLOWED_AUDIO_TYPES.join(',')}
            disabled={isUploading}
          />

          <div className="flex flex-col items-center justify-center gap-4 sm:gap-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-gradient-to-br from-orange-100 to-orange-50 flex items-center justify-center">
              <Upload className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-orange-500" />
            </div>

            <div className="space-y-2">
              <p className="text-lg sm:text-xl font-medium text-gray-700">Drop your files here</p>
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <span>or</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBrowseClick();
                  }}
                  className="text-primary hover:text-primary/80 font-medium flex items-center gap-1"
                >
                  <FolderOpen className="w-4 h-4" />
                  Browse
                </button>
              </div>
              <p className="text-xs sm:text-sm text-gray-500">Supported formats: MP3, WAV (max 50MB)</p>
            </div>
          </div>
        </div>

        {/* Status and Progress Section */}
        <div className="mt-4 sm:mt-6 space-y-4">
          {getStatusDisplay()}
          {uploadProgress > 0 && processingStatus === 'uploading' && (
            <div className="space-y-2">
              <div className="flex justify-between text-xs sm:text-sm text-gray-600">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-1.5 sm:h-2" />
            </div>
          )}

          {processingStatus === 'error' && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={resetUpload}
                className="gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Retry Upload
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}